import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Display, BootLoader, writeMfaLocalHint } from '@pc/ui';
import { readMfaClaims } from '@pc/db';
import { useAuth } from './AuthProvider';

// /auth/callback
//
// Destination of the hand-off URL the central auth site
// (auth.peptideclients.com) redirects users to after a successful
// sign-in / sign-up / password-reset / invitation flow. See the matching
// component in apps/web/src/auth/AuthCallbackPage.tsx for the full
// design notes -- this file is intentionally a near-duplicate so each
// app owns its own copy of the redirect logic (avoids an unnecessary
// cross-package dependency on a 60-line helper).
//
// IMPORTANT: this component MUST live OUTSIDE RequireAuth -- when we
// arrive here, by definition no session exists in this app yet.
export function AuthCallbackPage() {
  const nav = useNavigate();
  const { session, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (session && session.access_token) {
      const returnTo = safeReturnPath(searchParams.get('return_to'));
      const mfaVerifiedHint = searchParams.get('mfa_verified') === '1';
      const finish = (): void => {
        try {
          window.history.replaceState({}, document.title, returnTo);
        } catch {
          /* ignored */
        }
        nav(returnTo, { replace: true });
      };
      if (mfaVerifiedHint) {
        void readMfaClaims().then((claims) => {
          if (claims?.auth_session_id) writeMfaLocalHint(claims.auth_session_id);
        }).catch(() => {}).finally(finish);
        return;
      }
      finish();
      return;
    }
    setError(
      "Sign-in didn't complete cleanly. Try signing in again from auth.peptideclients.com.",
    );
  }, [session, loading, searchParams, nav]);

  if (error) {
    return (
      <main className="min-h-screen grid place-items-center bg-paper px-4">
        <div className="w-full max-w-md text-center">
          <Display size="md">Sign-in <em>failed</em></Display>
          <p className="mt-3 text-sm text-ink-2">{error}</p>
          <p className="mt-6 text-sm">
            <a
              href="https://auth.peptideclients.com/login?audience=vendor"
              className="underline hover:text-clay"
            >
              Back to sign-in
            </a>
          </p>
        </div>
      </main>
    );
  }

  return <BootLoader label="Signing you in…" />;
}

function safeReturnPath(raw: string | null): string {
  if (!raw) return '/';
  if (typeof raw !== 'string') return '/';
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/';
  if (raw.startsWith('/auth/callback')) return '/';
  if (raw === '/mfa' || raw.startsWith('/mfa?')) return '/';
  if (raw === '/sign-in' || raw.startsWith('/sign-in?')) return '/';
  return raw;
}
